import type { SimulationCueSheetRow } from '@/types/player/simulation-cue-sheet.types';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';

import {
  enrichSimulationRowAdvanceFromPlaylist,
  isReturnPreviousAdvanceSettings,
  isShowInfoAdvanceSettings,
  isSimulationShowInfoMediaRow,
} from '@/helpers/show-info-advance.helper';
import {
  isSimulationReturnPreviousRow,
  isSimulationRowInAsideRunOfMain,
  resolveSimulationMainPointer,
  simulationRowKey,
} from '@/helpers/simulation-return-previous.helper';

const DEFAULT_ADVANCE_SETTINGS = { loopEnabled: false, onEnd: 'advance' as const };

/** Ignore playback-complete briefly after going on-air (preview layer remount / StrictMode can spuriously fire `ended`). */
export const SIMULATION_MIN_PLAYBACK_MS_BEFORE_END = 750;

export function shouldIgnoreSimulationPlaybackComplete(onAirStartedAtMs: number | null | undefined): boolean {
  if (onAirStartedAtMs == null) {
    return false;
  }
  return Date.now() - onAirStartedAtMs < SIMULATION_MIN_PLAYBACK_MS_BEFORE_END;
}

export type SimulationCuePlaylistContext = {
  scope?: string;
  slotId?: string;
  /** Stable cue-row id — disambiguates duplicate itemId appearances (consecutive intermediates). */
  cueItemId?: string;
};

export type SimulationSegmentAnchor = 'flow_start' | 'flow_end' | 'slot';

/** Operator-facing segment kinds for Control tab rows (distinct within same anchor). */
export type FlowControlSegmentKind =
  | 'pre_flow'
  | 'flow_intro'
  | 'flow_outro'
  | 'after_flow'
  | 'flow_transition'
  | 'flow_resume'
  | 'session'
  | 'break';

export function isFlowControlInBetweenSegmentKind(
  kind: string | null | undefined,
): kind is 'flow_transition' | 'flow_resume' {
  return kind === 'flow_transition' || kind === 'flow_resume';
}

/** Break slot playlists use media scope `slot`; operator segment kind is still `break`. */
export function cueScopeMatchesSegmentKind(scope: string | undefined, segmentKind: FlowControlSegmentKind): boolean {
  if (!scope) {
    return false;
  }
  if (scope === segmentKind) {
    return true;
  }
  return segmentKind === 'break' && scope === 'slot';
}

export function segmentKindToAnchor(kind: FlowControlSegmentKind): SimulationSegmentAnchor {
  if (kind === 'pre_flow' || kind === 'flow_intro') {
    return 'flow_start';
  }
  if (kind === 'flow_outro' || kind === 'after_flow') {
    return 'flow_end';
  }
  return 'slot';
}

export function isSimulationRowInSegment(
  row: SimulationCueSheetRow,
  anchor: SimulationSegmentAnchor,
  slotId?: string,
  segmentKind?: string,
): boolean {
  if (segmentKind && row.segmentKind !== segmentKind) {
    return false;
  }
  if (anchor === 'flow_start') {
    return row.segmentAnchor === 'flow_start';
  }
  if (anchor === 'flow_end') {
    return row.segmentAnchor === 'flow_end';
  }
  if (anchor === 'slot' && slotId) {
    return row.segmentAnchor === `slot:${slotId}` || row.slotId === slotId;
  }
  return false;
}

export function resolveSimulationSegmentFromRow(
  row: SimulationCueSheetRow | undefined,
): { anchor: SimulationSegmentAnchor; slotId?: string; segmentKind: string } | null {
  if (!row) {
    return null;
  }
  if (row.segmentAnchor === 'flow_start') {
    return { anchor: 'flow_start', segmentKind: row.segmentKind };
  }
  if (row.segmentAnchor === 'flow_end') {
    return { anchor: 'flow_end', segmentKind: row.segmentKind };
  }
  if (row.segmentAnchor.startsWith('slot:')) {
    return { anchor: 'slot', slotId: row.segmentAnchor.slice('slot:'.length), segmentKind: row.segmentKind };
  }
  if (row.slotId) {
    return { anchor: 'slot', slotId: row.slotId, segmentKind: row.segmentKind };
  }
  return null;
}

export function resolveSimulationSegmentPlayPointer(
  rows: SimulationCueSheetRow[],
  anchor: SimulationSegmentAnchor,
  slotId?: string,
  segmentKind?: string,
): number {
  const inSegment = rows
    .map((row, index) => ({ row, index }))
    .filter(({ row }) => isSimulationRowInSegment(row, anchor, slotId, segmentKind));
  const playable = inSegment.find(({ row }) => isSimulationRowAutoCueTarget(row));
  return playable?.index ?? -1;
}

export function simulationRowsMatchPlaylistContext(
  row: SimulationCueSheetRow,
  context: SimulationCuePlaylistContext,
): boolean {
  if (context.cueItemId && row.cueItemId && row.cueItemId !== context.cueItemId) {
    return false;
  }
  if (context.scope && row.scope !== context.scope) {
    return false;
  }
  if (context.slotId) {
    const rowSlotId =
      row.slotId ?? (row.segmentAnchor?.startsWith('slot:') ? row.segmentAnchor.slice('slot:'.length) : undefined);
    if (rowSlotId && rowSlotId !== context.slotId) {
      return false;
    }
  }
  return true;
}

/** Playlist context aligned with flow-control media rows (`scope` + flow slot id). */
export function simulationPlaylistContextFromRow(
  row: SimulationCueSheetRow | undefined,
): SimulationCuePlaylistContext | undefined {
  if (!row) {
    return undefined;
  }
  const scope =
    row.scope ?? (row.segmentKind === 'session' || row.segmentKind === 'break' ? row.segmentKind : undefined);
  const slotId =
    row.slotId ?? (row.segmentAnchor?.startsWith('slot:') ? row.segmentAnchor.slice('slot:'.length) : undefined);
  if (!scope && !slotId && !row.cueItemId) {
    return undefined;
  }
  return { scope, slotId, cueItemId: row.cueItemId };
}

export function resolveSimulationLastPointerInSegment(rows: SimulationCueSheetRow[], fromPointer: number): number {
  const row = rows[fromPointer];
  if (!row) {
    return fromPointer;
  }
  const segmentKey = `${row.segmentKind}|${row.segmentAnchor}|${row.slotId ?? ''}`;
  let last = fromPointer;
  for (let index = fromPointer + 1; index < rows.length; index += 1) {
    const candidate = rows[index]!;
    const candidateKey = `${candidate.segmentKind}|${candidate.segmentAnchor}|${candidate.slotId ?? ''}`;
    if (candidateKey !== segmentKey) {
      break;
    }
    last = index;
  }
  return last;
}

/** First playable cue row after the segment containing `fromPointer`. */
export function resolveSimulationAdvancePastSegment(rows: SimulationCueSheetRow[], fromPointer: number): number | null {
  const lastInSegment = resolveSimulationLastPointerInSegment(rows, fromPointer);
  return resolveAdjacentSimulationPointer(rows, lastInSegment, 1);
}

export function findSimulationRowIndex(
  rows: SimulationCueSheetRow[],
  itemId: string,
  context?: SimulationCuePlaylistContext,
  pointer?: number,
): number {
  if (context?.cueItemId) {
    const byCueItemId = rows.findIndex((row) => row.cueItemId === context.cueItemId);
    if (byCueItemId >= 0) {
      return byCueItemId;
    }
  }

  const matches = rows.map((row, index) => ({ row, index })).filter(({ row }) => row.itemId === itemId);

  if (matches.length === 0) {
    return -1;
  }
  if (matches.length === 1) {
    return matches[0]!.index;
  }

  if (context?.scope || context?.slotId) {
    const scoped = matches.find(({ row }) => simulationRowsMatchPlaylistContext(row, context));
    if (scoped) {
      return scoped.index;
    }
  }

  const playable = matches.filter(({ row }) => isSimulationRowPlayable(row));
  if (playable.length === 0) {
    return matches[0]!.index;
  }
  if (pointer == null) {
    return playable[0]!.index;
  }

  return playable.reduce((best, current) =>
    Math.abs(current.index - pointer) < Math.abs(best.index - pointer) ? current : best,
  ).index;
}

export function isSimulationRowPlayable(row: SimulationCueSheetRow): boolean {
  if (row.isPlayable === false) {
    return false;
  }
  if (row.isAutoOnly) {
    if (row.presentationKind === 'agent') {
      if (row.isPlayable === true) {
        return true;
      }
      return Boolean(row.fallbackCompositeItemId);
    }
    return (
      row.presentationKind === 'composite' ||
      row.itemId.startsWith('composite:') ||
      row.itemId.startsWith('virtualMedia:')
    );
  }
  if (row.presentationKind === 'agent') {
    if (row.isPlayable === true) {
      return true;
    }
    return Boolean(row.fallbackCompositeItemId);
  }
  return true;
}

/** Playable row that auto-cue navigation may land on (excludes playlist skip + consumed asides). */
export function isSimulationRowAutoCueTarget(
  row: SimulationCueSheetRow,
  index?: number,
  playedReturnPreviousKeys?: ReadonlySet<string>,
): boolean {
  if (!isSimulationRowPlayable(row) || row.operatorSkipped) {
    return false;
  }
  if (playedReturnPreviousKeys && index != null) {
    const key = simulationRowKey(row, index);
    if (playedReturnPreviousKeys.has(key)) {
      return false;
    }
  }
  return true;
}

export function resolveInitialSimulationPointer(rows: SimulationCueSheetRow[]): number {
  const index = rows.findIndex((row, i) => isSimulationRowAutoCueTarget(row, i));
  return index >= 0 ? index : 0;
}

export function resolveAdjacentSimulationPointer(
  rows: SimulationCueSheetRow[],
  fromPointer: number,
  delta: number,
  playedReturnPreviousKeys?: ReadonlySet<string>,
): number | null {
  if (rows.length === 0 || delta === 0) {
    return null;
  }
  const direction = delta > 0 ? 1 : -1;
  let index = fromPointer + direction;
  while (index >= 0 && index < rows.length) {
    if (isSimulationRowAutoCueTarget(rows[index]!, index, playedReturnPreviousKeys)) {
      return index;
    }
    index += direction;
  }
  return null;
}

export function buildSimulationSelectedItemIds(
  rows: SimulationCueSheetRow[],
  pointer: number,
  extraItemIds: string[] = [],
): string[] {
  const current = rows[pointer]?.itemId;
  const ids = new Set(extraItemIds);
  if (current) {
    ids.add(current);
  }
  return [...ids];
}

const INFO_FALLBACK_PRIORITY = ['session-info', 'break-info', 'flow-info', 'event-info'] as const;

function compositeSlugFromItemId(itemId: string): string | null {
  if (!itemId.startsWith('composite:')) {
    return null;
  }
  const slug = itemId.slice('composite:'.length);
  return slug.length > 0 ? slug : null;
}

function isInfoCompositeItemId(itemId: string): boolean {
  const slug = compositeSlugFromItemId(itemId);
  return slug != null && slug.includes('-info');
}

function infoCompositePriority(slug: string): number {
  const index = INFO_FALLBACK_PRIORITY.indexOf(slug as (typeof INFO_FALLBACK_PRIORITY)[number]);
  return index === -1 ? 999 : index;
}

function findPlayableInfoRowBySlug(rows: SimulationCueSheetRow[], slug: string): SimulationCueSheetRow | undefined {
  return rows.find((row) => row.isPlayable !== false && compositeSlugFromItemId(row.itemId) === slug);
}

function getInfoFallbackCascadeForRow(row: SimulationCueSheetRow): string[] {
  const cascade: string[] = [];
  if (row.segmentKind === 'session' || row.scope === 'session') {
    cascade.push('session-info');
  } else if (row.segmentKind === 'break' || row.scope === 'slot') {
    cascade.push('break-info');
  }
  cascade.push('flow-info', 'event-info');
  return cascade;
}

/** Scope-idle play target when stopping media within a segment (enabled info virtual, else cascade fallback). */
export function resolveScopeIdlePlayItemId(rows: SimulationCueSheetRow[], pointer: number): string | null {
  const current = rows[pointer];
  if (!current) {
    return null;
  }
  const segmentKey = `${current.segmentKind}|${current.segmentAnchor}|${current.slotId ?? ''}`;

  const segmentInfoItemIds = rows
    .filter((row) => {
      const key = `${row.segmentKind}|${row.segmentAnchor}|${row.slotId ?? ''}`;
      if (key !== segmentKey) {
        return false;
      }
      if (row.isPlayable === false) {
        return false;
      }
      return isInfoCompositeItemId(row.itemId);
    })
    .map((row) => row.itemId);

  if (segmentInfoItemIds.length > 0) {
    return [...segmentInfoItemIds].sort((a, b) => {
      const slugA = compositeSlugFromItemId(a)!;
      const slugB = compositeSlugFromItemId(b)!;
      return infoCompositePriority(slugA) - infoCompositePriority(slugB);
    })[0]!;
  }

  const segmentRows = rows.filter(
    (row) => `${row.segmentKind}|${row.segmentAnchor}|${row.slotId ?? ''}` === segmentKey,
  );

  for (const slug of getInfoFallbackCascadeForRow(current)) {
    const inSegment = findPlayableInfoRowBySlug(segmentRows, slug);
    if (inSegment) {
      return inSegment.itemId;
    }

    if (slug === 'flow-info' || slug === 'event-info') {
      const global = findPlayableInfoRowBySlug(rows, slug);
      if (global) {
        return global.itemId;
      }
    }
  }

  return current.fallbackCompositeItemId ?? null;
}

/** Synthetic room cam for Broadcast simulation when show mode needs a cam slot and none is staged. */
export const SIMULATION_ROOM_CAM_PLACEHOLDER_ID = 'roomCam:simulation-placeholder-cam1';

/** Ensure Broadcast (onlinePlayer) preview requests include a roomCam id for both/speakerOnly. */
export function ensureSimulationBroadcastRoomCamIds(
  selectedItemIds: string[],
  showMode: 'mediaOnly' | 'both' | 'speakerOnly',
): string[] {
  if (showMode !== 'both' && showMode !== 'speakerOnly') {
    return selectedItemIds;
  }
  if (selectedItemIds.some((id) => id.startsWith('roomCam:'))) {
    return selectedItemIds;
  }
  return [...selectedItemIds, SIMULATION_ROOM_CAM_PLACEHOLDER_ID];
}

/** Item ids sent to simulation preview layers — current cue row plus mixed/speaker cams / active fallback. */
export function buildSimulationPreviewItemIds(params: {
  rows: SimulationCueSheetRow[];
  pointer: number;
  selectedItemIds: string[];
  showMode: 'mediaOnly' | 'both' | 'speakerOnly';
  /**
   * Broadcast (onlinePlayer) simulation: ensure a roomCam id is present for both/speakerOnly so the
   * backend can build PiP / speaker layouts with a placeholder cam layer.
   */
  ensureRoomCamPlaceholder?: boolean;
}): string[] {
  const current = params.rows[params.pointer];
  if (!current?.itemId) {
    return [];
  }

  const showingFallbackOnly =
    Boolean(current.fallbackCompositeItemId) &&
    params.selectedItemIds.includes(current.fallbackCompositeItemId!) &&
    !params.selectedItemIds.includes(current.itemId);

  const ids = new Set<string>();
  if (showingFallbackOnly) {
    ids.add(current.fallbackCompositeItemId!);
  } else {
    ids.add(current.itemId);
    if (current.fallbackCompositeItemId && params.selectedItemIds.includes(current.fallbackCompositeItemId)) {
      ids.add(current.fallbackCompositeItemId);
    }
  }

  if (params.showMode === 'both' || params.showMode === 'speakerOnly') {
    for (const id of params.selectedItemIds) {
      if (id.startsWith('remoteSpeaker:') || id.startsWith('roomCam:')) {
        ids.add(id);
      }
    }
  }

  const result = [...ids];
  return params.ensureRoomCamPlaceholder ? ensureSimulationBroadcastRoomCamIds(result, params.showMode) : result;
}

export type SimulationPlaybackCompleteResult =
  | { action: 'none' }
  | { action: 'stop' }
  | { action: 'scope_idle' }
  | { action: 'advance'; pointer: number }
  | { action: 'return_previous' };

/** Mirrors live orchestrator playback-complete rules for flow-control simulation. */
export function resolveSimulationPlaybackComplete(
  rows: SimulationCueSheetRow[],
  pointer: number,
  itemId: string,
  flow?: FlowDto,
): SimulationPlaybackCompleteResult {
  const current = rows[pointer];
  if (!current || current.itemId !== itemId) {
    return { action: 'none' };
  }

  const rowForAdvance = flow ? enrichSimulationRowAdvanceFromPlaylist(flow, current) : current;
  const settings = rowForAdvance.advanceSettings ?? DEFAULT_ADVANCE_SETTINGS;

  if (isReturnPreviousAdvanceSettings(settings) || isSimulationReturnPreviousRow(rowForAdvance, flow)) {
    return { action: 'return_previous' };
  }

  if (isShowInfoAdvanceSettings(settings) || isSimulationShowInfoMediaRow(rowForAdvance, flow)) {
    return { action: 'scope_idle' };
  }

  if (settings.onEnd === 'advance' && rowForAdvance.isLastInScopePlaylist) {
    return { action: 'stop' };
  }

  if (settings.onEnd === 'advance') {
    const nextPointer = resolveAdjacentSimulationPointer(rows, pointer, 1);
    if (nextPointer == null) {
      return { action: 'stop' };
    }
    return { action: 'advance', pointer: nextPointer };
  }

  return { action: 'none' };
}

export function shouldSimulationLayerLoop(row: SimulationCueSheetRow | undefined): boolean {
  if (!row?.advanceSettings?.loopEnabled) {
    return false;
  }
  return row.fileType === 'imageSequence';
}

export type SimulationStepMediaResult =
  | { action: 'none' }
  | { action: 'advance'; pointer: number }
  | { action: 'replay'; pointer: number }
  | { action: 'scope_idle'; pointer: number }
  | {
      action: 'enter_aside';
      asidePointer: number;
      returnTargetPointer: number;
      markPlayed: boolean;
    }
  | { action: 'leave_aside'; returnPointer: number; asideKey: string; markPlayed: boolean };

export type SimulationStepMediaOptions = {
  overridePointer?: number | null;
  returnTargetRowKey?: string | null;
  playedReturnPreviousKeys?: readonly string[];
};

export function simulationScopeIdleSelection(
  rows: SimulationCueSheetRow[],
  pointer: number,
): { selectedItemIds: string[]; onAirStartedAtMs: number | null } {
  const scopeIdleItemId = resolveScopeIdlePlayItemId(rows, pointer);
  if (!scopeIdleItemId) {
    return { selectedItemIds: [], onAirStartedAtMs: null };
  }
  return { selectedItemIds: [scopeIdleItemId], onAirStartedAtMs: Date.now() };
}

export function isSimulationScopeIdleShowing(
  rows: SimulationCueSheetRow[],
  pointer: number,
  selectedItemIds: string[],
): boolean {
  const currentRow = rows[pointer];
  if (!currentRow) {
    return false;
  }
  const scopeIdleId = resolveScopeIdlePlayItemId(rows, pointer);
  return Boolean(scopeIdleId && selectedItemIds.includes(scopeIdleId) && !selectedItemIds.includes(currentRow.itemId));
}

/** Mirrors live stepMedia before advancing to the next row. */
export function resolveSimulationStepMedia(
  rows: SimulationCueSheetRow[],
  pointer: number,
  selectedItemIds: string[],
  delta: number,
  flow?: FlowDto,
  options?: SimulationStepMediaOptions,
): SimulationStepMediaResult {
  const playedKeys = new Set(options?.playedReturnPreviousKeys ?? []);
  const overridePointer = options?.overridePointer ?? null;

  // Planned aside on air: next/prev media returns to the stashed main cue item.
  if (overridePointer != null && options?.returnTargetRowKey) {
    const asideRow = rows[overridePointer];
    if (asideRow && isSimulationReturnPreviousRow(asideRow, flow)) {
      const returnPointer = rows.findIndex((row, i) => simulationRowKey(row, i) === options.returnTargetRowKey) ?? -1;
      const resolvedReturn =
        returnPointer >= 0 ? returnPointer : (resolveSimulationMainPointer(rows, pointer, flow) ?? pointer);
      const mainIndex = resolveSimulationMainPointer(rows, resolvedReturn, flow) ?? resolvedReturn;
      const asideKey = simulationRowKey(asideRow, overridePointer);
      return {
        action: 'leave_aside',
        returnPointer: resolvedReturn,
        asideKey,
        markPlayed: isSimulationRowInAsideRunOfMain(rows, mainIndex, overridePointer, flow),
      };
    }
  }

  const currentRow = rows[pointer];
  const scopeIdleShowing = isSimulationScopeIdleShowing(rows, pointer, selectedItemIds);
  const rowForAdvance = flow && currentRow ? enrichSimulationRowAdvanceFromPlaylist(flow, currentRow) : currentRow;

  if (delta > 0 && isSimulationShowInfoMediaRow(rowForAdvance, flow) && !scopeIdleShowing) {
    return { action: 'scope_idle', pointer };
  }

  if (scopeIdleShowing) {
    if (delta < 0) {
      return { action: 'replay', pointer };
    }
    if (delta > 0) {
      const nextPointer = resolveAdjacentSimulationPointer(rows, pointer, 1, playedKeys);
      if (nextPointer == null) {
        return { action: 'none' };
      }
      return { action: 'advance', pointer: nextPointer };
    }
    return { action: 'none' };
  }

  const nextPointer = resolveAdjacentSimulationPointer(rows, pointer, delta, playedKeys);
  if (nextPointer == null) {
    return { action: 'none' };
  }

  // Cue-order enter into a return_previous aside (keep auto pointer on main).
  if (
    delta > 0 &&
    nextPointer !== pointer &&
    !isSimulationReturnPreviousRow(currentRow, flow) &&
    isSimulationReturnPreviousRow(rows[nextPointer], flow)
  ) {
    const returnTargetPointer = resolveSimulationMainPointer(rows, pointer, flow) ?? pointer;
    return {
      action: 'enter_aside',
      asidePointer: nextPointer,
      returnTargetPointer,
      markPlayed: false,
    };
  }

  return { action: 'advance', pointer: nextPointer };
}
